import { useState } from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import { Button } from '@localagent/ui/components/button';
import type { CurrentPeriodPosition } from '@localagent/sdk';

export interface PaymentActionRequiredProps {
  position: CurrentPeriodPosition;
  onRetryAdvanceCharge?: () => void | Promise<void>;
  isRetrying?: boolean;
  /** When true, render Stripe PaymentElement for card update before retry. */
  showPaymentElement?: boolean;
}

function RetryWithPaymentElement({
  onRetryAdvanceCharge,
  isRetrying,
}: {
  onRetryAdvanceCharge: () => void | Promise<void>;
  isRetrying: boolean;
}) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleRetry = async () => {
    setLocalError(null);
    setProcessing(true);
    try {
      await onRetryAdvanceCharge();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setProcessing(false);
    }
  };

  const busy = isRetrying || processing;

  return (
    <>
      <div data-testid="billing-retry-payment-element">
        <PaymentElement
          options={{
            layout: 'tabs',
            wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          }}
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void handleRetry();
          }}
          disabled={busy}
          data-testid="billing-retry-advance-charge"
        >
          {busy ? 'Retrying…' : 'Retry Payment'}
        </Button>
      </div>
      {localError ? (
        <p className="text-sm text-destructive" data-testid="billing-retry-error">
          {localError}
        </p>
      ) : null}
    </>
  );
}

export function PaymentActionRequired({
  position,
  onRetryAdvanceCharge,
  isRetrying = false,
  showPaymentElement = false,
}: PaymentActionRequiredProps) {
  const { status } = position;

  if (status === 'awaiting_payment') {
    return (
      <div
        className="rounded-lg bg-yellow-500/10 p-4 space-y-3"
        data-testid="billing-advance-declined"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg
              className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-yellow-800 dark:text-yellow-300">
              Payment Required
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
              Your billing period could not be opened because the advance charge
              was declined. Please update your payment method and retry.
            </p>
          </div>
        </div>
        {showPaymentElement && onRetryAdvanceCharge ? (
          <RetryWithPaymentElement
            onRetryAdvanceCharge={onRetryAdvanceCharge}
            isRetrying={isRetrying}
          />
        ) : (
          <div className="flex gap-2">
            {onRetryAdvanceCharge && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void onRetryAdvanceCharge();
                }}
                disabled={isRetrying}
                data-testid="billing-retry-advance-charge"
              >
                {isRetrying ? 'Retrying…' : 'Retry Payment'}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (status === 'settlement_failed') {
    return (
      <div
        className="rounded-lg bg-red-500/10 p-4 space-y-3"
        data-testid="billing-settlement-declined"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg
              className="h-5 w-5 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-red-800 dark:text-red-300">
              Settlement Declined
            </h3>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">
              Your overage settlement for the previous period was declined.
              Please update your payment method. We will retry the charge
              automatically.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
